import { AddOutlined, ClearRounded, RefreshRounded, SaveOutlined } from '@mui/icons-material';
import { Box, Card, CardContent, TextField, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import { AnalyticsEvent, useAnalytics } from '../../../../components/Providers/AnalyticsProvider';
import { useDrawer } from '../../../../components/Providers/DrawerProvider';
import { selectFolders } from '../../../../redux/reducers/folderReducer';
import { insertProjectKpiQuestion, processQuestion } from '../../../../services/dealGPT';
import { StyledIconButton } from '../../../../theme/CustomComponents';
import { ProjectRunAnalysisQuestion } from '../../../../Types/dealGPT';
import { ProcessReason } from '../../../../Types/enums';
import KpiQuestionAccordion from './KpiQuestionAccordion';

interface KpiQuestionsCardProps {
  title: string;
  runId: string;
  canAddKpis?: boolean;
  kpiQuestions: ProjectRunAnalysisQuestion[];
  refetchProjectConfig: () => Promise<void>;
}

const KpiQuestionsCard: React.FC<KpiQuestionsCardProps> = ({
  title,
  runId,
  canAddKpis = false,
  kpiQuestions,
  refetchProjectConfig,
}) => {
  const [questions, setQuestions] = useState<ProjectRunAnalysisQuestion[]>([]);
  const [newlyCreatedId, setNewlyCreatedId] = useState<string | null>(null);
  const [questionToAdd, setQuestionToAdd] = useState<string | undefined>(undefined);
  const [processingQuestions, setProcessingQuestions] = useState<
    ProjectRunAnalysisQuestion[] | undefined
  >(undefined);
  const { closeDrawer, addCanClose } = useDrawer();
  const analytics = useAnalytics();
  const { currentFolder } = useSelector(selectFolders);

  const sortedQuestions = useMemo(
    () =>
      [...questions].sort((a, b) => (a.prettyQuestion ?? '').localeCompare(b.prettyQuestion ?? '')),
    [questions]
  );

  const onAddQuestion = useCallback(() => {
    analytics.event(
      AnalyticsEvent.WM_IA_PROJECT_OVERVIEW_KPI_ADD,
      {
        id: currentFolder?.projectId,
      },
      {
        projectId: currentFolder?.projectId,
      }
    );
    setQuestionToAdd('');
  }, [currentFolder?.projectId, analytics]);

  const handleSubmit = useCallback(async () => {
    analytics.event(
      AnalyticsEvent.WM_IA_PROJECT_OVERVIEW_KPI_SAVE,
      {
        id: currentFolder?.projectId,
      },
      {
        projectId: currentFolder?.projectId,
      }
    );
    if (questionToAdd && currentFolder?.projectId) {
      const inserted = await insertProjectKpiQuestion(
        { projectId: currentFolder.projectId, question: questionToAdd, deleted: false },
        runId,
        questions.length + 1
      );

      if (inserted) {
        try {
          const newQuestion = { ...inserted, isNew: false };
          setQuestions((prev) => [newQuestion, ...prev]);
          setQuestionToAdd(undefined);
          setNewlyCreatedId(inserted.id);
          setProcessingQuestions([newQuestion]);
          await processQuestion(inserted, ProcessReason.PROCESS_THIS);
          await refetchProjectConfig();
        } catch (error) {
          enqueueSnackbar('Failed to process new Basic Fact. Please try again later.', {
            variant: 'error',
          });
          setQuestions((prev) => prev.filter((q) => q.id !== inserted.id));
        } finally {
          setProcessingQuestions(undefined);
        }
      }
    }

    setQuestionToAdd(undefined);
  }, [currentFolder?.projectId, runId, questions, questionToAdd, refetchProjectConfig, analytics]);

  const handleReprocessAll = useCallback(async () => {
    if (sortedQuestions.length === 0) return;

    setProcessingQuestions(sortedQuestions);

    await Promise.all(
      sortedQuestions.map((question) =>
        processQuestion(question, ProcessReason.PROCESS_THIS).then(() => {
          refetchProjectConfig();
          setProcessingQuestions((prev) => (prev ?? []).filter((q) => q.id !== question.id));
        })
      )
    );

    enqueueSnackbar('Basic Facts reprocessed successfully', { variant: 'success' });
    setProcessingQuestions(undefined);
  }, [sortedQuestions, refetchProjectConfig]);

  useEffect(() => {
    setQuestions(kpiQuestions);
  }, [kpiQuestions]);

  // Prevent the user from closing the drawer while processing a KPI
  useEffect(() => {
    if (!processingQuestions) return;

    const removeCanClose = addCanClose(() => {
      enqueueSnackbar('Cannot close the drawer while processing a KPI', {
        variant: 'error',
        autoHideDuration: 3000,
      });
      return false;
    });

    return () => removeCanClose();
  }, [processingQuestions, addCanClose]);

  return (
    <Card
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent>
        <Form
          onSubmit={handleSubmit}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="h5" sx={{ color: '#f900d3' }}>
                    {title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Reprocess Basic Facts" arrow>
                      <StyledIconButton
                        onClick={handleReprocessAll}
                        disabled={!!processingQuestions}
                        type="button"
                      >
                        <RefreshRounded />
                      </StyledIconButton>
                    </Tooltip>
                    {canAddKpis && (
                      <Tooltip title="New Basic Fact" arrow>
                        <StyledIconButton
                          onClick={onAddQuestion}
                          disabled={!!processingQuestions}
                          type="button"
                        >
                          <AddOutlined />
                        </StyledIconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    padding: 1,
                  }}
                >
                  {questionToAdd !== undefined && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        label="Enter Basic Fact Question"
                        value={questionToAdd}
                        onChange={(e) => setQuestionToAdd(e.target.value)}
                        required
                        fullWidth
                        size="small"
                        variant="outlined"
                        autoFocus
                      />
                      <Tooltip title="Save" arrow>
                        <StyledIconButton onClick={handleSubmit} disabled={!questionToAdd}>
                          <SaveOutlined />
                        </StyledIconButton>
                      </Tooltip>
                      <Tooltip title="Cancel" arrow>
                        <StyledIconButton onClick={() => setQuestionToAdd(undefined)}>
                          <ClearRounded />
                        </StyledIconButton>
                      </Tooltip>
                    </Box>
                  )}
                  {sortedQuestions.map((question) => (
                    <KpiQuestionAccordion
                      key={question.id}
                      autoScroll={newlyCreatedId === question.id}
                      isProcessing={
                        processingQuestions?.some(
                          (q) => q.prettyQuestion === question.prettyQuestion
                        ) ?? false
                      }
                      question={question}
                      closeDrawer={closeDrawer}
                      refetchProjectConfig={refetchProjectConfig}
                    />
                  ))}
                </Box>
              </Box>
            </form>
          )}
        />
      </CardContent>
    </Card>
  );
};

export default KpiQuestionsCard;
